import React, { useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from './SEOHead';
import Nav from './Nav';
import HeroSection from './HeroSection';
import StatsCounter from './StatsCounter';
import BenefitsGrid from './BenefitsGrid';
import top31 from '../assets/image/top-31.png';
import top32 from '../assets/image/top-32.png';
import top33 from '../assets/image/top-33.png';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Lazy load ALL non-critical components for better performance
const LazyBrandStatsSection = lazy(() => import('./BrandStatsSection'));
const LazyReviewsSection = lazy(() => import('./ReviewsSection'));
const LazyAIHowItWorks = lazy(() => import('./AIHowItWorks'));
const LazyQualitativeResearchSection = lazy(() => import('./QualitativeResearchSection'));
const LazyFileFormatsSection = lazy(() => import('./FileFormatsSection'));
const LazyScrollWrap = lazy(() => import('./ScrollWrap'));
const LazyFeaturesGrid = lazy(() => import('./FeaturesGrid'));
const LazyFAQSection = lazy(() => import('./FAQSection'));
const LazyFooter = lazy(() => import('./Footer'));

// Loading component
const LoadingSection = () => (
  <div style={{ 
    height: '200px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    background: '#f8f9fa' 
  }}>
    <div style={{ color: '#6c757d', fontSize: '14px' }}>Loading...</div>
  </div>
);

function HomePage() {
  const { lang } = useParams();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    if (lang && (lang === 'en' || lang === 'ar')) {
      setLanguage(lang);
    }
  }, [lang, setLanguage]);

  // Initialize AOS for scroll animations
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: true,
      offset: 100
    });
  }, []);

  // SEO content optimized for 'audiosum' keyword 
  const seoContent = {
    en: {
      title: "Audiosum | AI Summary, Document, Audio Summary & Video Transcription",
      description: "Convert audio and video to text with 99%+ accuracy in 95+ languages. AI-powered summary and transcription made easy with AI summary tool.",
      keywords: "audiosum, audio transcription, AI transcription, speech to text, audio summarization, AI audio analysis"
    },
    ar: {
      title: "لخص - أداة تلخيص بالذكاء الاصطناعي | تحويل الصوت والفيديو إلى نص مجاناً",
      description: "لخص - منصة تلخيص متطورة لتحويل الصوت إلى نص مجاناً. أداة تلخيص ذكية بالذكاء الاصطناعي لمعالجة المحتوى واستخراج تلخيص دقيق في 95+ لغة.",
      keywords: "لخص، تلخيص، أداة تلخيص، تحويل الصوت، تحويل بالذكاء الاصطناعي، تحويل الكلام إلى نص، تلخيص الصوت، تحليل الصوت"
    }
  };

  const currentLang = lang || 'en';

  return (
    <div>
      <SEOHead 
        title={seoContent[currentLang].title}
        description={seoContent[currentLang].description}
        keywords={seoContent[currentLang].keywords}
      />
  <Nav />
  <HeroSection />
      
      {/* Top 3 Features Section - Vertical Scroll Animation */}
      <section style={{ 
        padding: '100px 40px',
        background: '#000000',
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10 mb-5" data-aos="fade-up" data-aos-duration="800">
              <img 
                src={top31} 
                alt="Audiosum Feature 1" 
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '24px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
                }} 
              />
            </div>
            <div className="col-lg-8 col-md-10 mb-5" data-aos="fade-up" data-aos-duration="800">
              <img 
                src={top32} 
                alt="Audiosum Feature 2" 
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '24px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
                }} 
              />
            </div>
            <div className="col-lg-8 col-md-10 mb-5" data-aos="fade-up" data-aos-duration="800">
              <img 
                src={top33} 
                alt="Audiosum Feature 3" 
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '24px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
                }} 
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Counter Section - Moved to top after Top 3 Features */}
      <StatsCounter 
        showHeading={true}
        title="Trusted by Thousands Worldwide"
        subtitle="Helping students and professionals save thousands of hours of manual work"
      />
      
      <BenefitsGrid />
      
      <Suspense fallback={<LoadingSection />}>
        <LazyAIHowItWorks />
      </Suspense>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyBrandStatsSection />
      </Suspense>
      
      {/* Keep ScrollWrap but remove SliderWrap to optimize word count */}
      <Suspense fallback={<LoadingSection />}>
        <LazyScrollWrap />
      </Suspense>
      
      <div style={{ marginBottom: '2rem' }}></div>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyQualitativeResearchSection />
      </Suspense>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyFeaturesGrid />
      </Suspense>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyFileFormatsSection />
      </Suspense>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyFAQSection />
      </Suspense>
      
      {/* Reviews Section - Positioned after FAQ section */}
      <div className="container px-4" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
        <Suspense fallback={<LoadingSection />}>
          <LazyReviewsSection />
        </Suspense>
      </div>
      
      {/* Simple CTA Section with Features */}
      <div className="container-fluid mb-5" style={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
        padding: '80px 0' 
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h2 className="fw-bold mb-4" style={{ color: '#1e293b', fontSize: '2.5rem' }}>
                {language === 'ar' ? 'ابدأ تجربتك المجانية اليوم' : 'Start Your Free Trial Today'}
              </h2>
              <p className="lead mb-5" style={{ color: '#64748b', fontSize: '1.2rem' }}>
                {language === 'ar' 
                  ? 'اكتشف قوة معالجة المستندات الذكية مع التجربة المجانية الشاملة • لا حاجة لبطاقة ائتمان'
                  : 'Discover the power of intelligent document processing with comprehensive free trial • No credit card required'
                }
              </p>
              
              {/* Feature Cards */}
              <div className="row mb-5">
                <div className="col-md-4 mb-3">
                  <div className="d-flex flex-column align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mb-3" 
                         style={{ width: '60px', height: '60px', background: '#0695FF' }}>
                      <i className="fas fa-credit-card text-white"></i>
                    </div>
                    <h6 className="fw-bold mb-2" style={{ color: '#1e293b' }}>
                      {language === 'ar' ? 'لا حاجة لبطاقة ائتمان' : 'No credit card required'}
                    </h6>
                  </div>
                </div>
                
                <div className="col-md-4 mb-3">
                  <div className="d-flex flex-column align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mb-3" 
                         style={{ width: '60px', height: '60px', background: '#0695FF' }}>
                      <i className="fas fa-clock text-white"></i>
                    </div>
                    <h6 className="fw-bold mb-2" style={{ color: '#1e293b' }}>
                      {language === 'ar' ? 'دقائق للبدء' : 'Minutes to get started'}
                    </h6>
                  </div>
                </div>
                
                <div className="col-md-4 mb-3">
                  <div className="d-flex flex-column align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mb-3" 
                         style={{ width: '60px', height: '60px', background: '#0695FF' }}>
                      <i className="fas fa-headset text-white"></i>
                    </div>
                    <h6 className="fw-bold mb-2" style={{ color: '#1e293b' }}>
                      {language === 'ar' ? 'دعم فني على مدار الساعة' : '24/7 technical support'}
                    </h6>
                  </div>
                </div>
              </div>
              
              {/* CTA Button */}
              <a 
                href={`https://audiosum.app/signup?lang=${language}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-lg px-5"
                style={{ 
                  borderRadius: '25px',
                  background: '#0695FF',
                  border: 'none',
                  padding: '14px 40px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 8px 30px rgba(6, 149, 255, 0.4)',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#0578D6';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 40px rgba(6, 149, 255, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#0695FF';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 30px rgba(6, 149, 255, 0.4)';
                }}
              >
                {language === 'ar' ? 'ابدأ مجاناً' : 'Start Free Trial'}
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Suspense fallback={<LoadingSection />}>
        <LazyFooter />
      </Suspense>
    </div>
  );
}

export default HomePage;